<template>
  <v-dialog v-model="localModelValue" max-width="716px" persistent>
    <v-card class="trainer-detail-modal">
      <div class="modal-content">
        <div class="modal-header">
          <div class="header-text">
            <h2 class="modal-title">
              Trainer Details -
              {{ trainerDetails?.fullname || trainer?.name || "" }}
            </h2>
            <p class="modal-subtitle">
              View detailed information about trainer progress and module
              completion status.
            </p>
          </div>
        </div>

        <div class="trainer-info-section">
          <div class="info-grid">
            <div class="info-item">
              <p class="info-label">Email</p>
              <p class="info-value">{{ trainerDetails?.email || "-" }}</p>
            </div>
            <div class="info-item">
              <p class="info-label">Trainer ID</p>
              <p class="info-value">{{ trainerDetails?.user_id || "-" }}</p>
            </div>
            <div class="info-item">
              <p class="info-label">Registration Date</p>
              <p class="info-value">{{ formattedRegistrationDate }}</p>
            </div>
            <div class="info-item">
              <p class="info-label">Overall Progress</p>
              <p class="info-value">{{ trainerDetails?.progress || 0 }}%</p>
            </div>
          </div>
        </div>

        <div class="module-progress-section">
          <h3 class="section-title">Module Progress</h3>
          <div v-if="loading" class="modules-list">
            <div
              v-for="n in 3"
              :key="n"
              class="module-item skeleton-module-item"
            >
              <div class="module-info">
                <div class="skeleton-module-number"></div>
                <div class="skeleton-module-title"></div>
              </div>
              <div class="skeleton-module-status"></div>
            </div>
          </div>
          <div v-else-if="modules.length > 0" class="modules-list">
            <div
              v-for="(module, index) in modules"
              :key="module.id || index"
              class="module-item"
            >
              <div class="module-info">
                <p class="module-number">Module {{ index + 1 }}</p>
                <p class="module-title">{{ module.title }}</p>
              </div>
              <span
                class="module-status"
                :class="
                  module.status === 'Completed'
                    ? 'status-completed'
                    : 'status-in-progress'
                "
              >
                {{ module.status }}
              </span>
            </div>
          </div>
          <div v-else class="no-modules">
            <p>No modules found</p>
          </div>
        </div>

        <div class="certificate-section">
          <ActionButton
            class="download-certificate-button"
            variant="primary"
            :disabled="!canDownload || isDownloadingCertificate"
            :loading="isDownloadingCertificate"
            loading-text="Downloading..."
            @click="downloadCertificate"
          >
            <template #icon-left>
              <DownloadIcon
                :color="
                  canDownload && !isDownloadingCertificate
                    ? '#003d6a'
                    : '#cccccc'
                "
              />
            </template>
            Download Certificate
          </ActionButton>
        </div>

        <div class="modal-footer">
          <button class="close-button" @click="closeModal">Close</button>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref, watch } from "vue";
import DownloadIcon from "@/components/icons/DownloadIcon.vue";
import trainerAcademyService from "@/services/trainerAcademyService";
import ActionButton from "@/components/common/ActionButton.vue";
import { getApiError } from "@/utils/apiError";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  trainer: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(["update:modelValue", "close"]);

const localModelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const trainerDetails = ref(null);
const loading = ref(false);
const isDownloadingCertificate = ref(false);

watch(
  () => props.modelValue,
  async (isOpen) => {
    if (isOpen && props.trainer?.user_id) {
      await fetchTrainerDetails();
    } else if (!isOpen) {
      trainerDetails.value = null;
    }
  }
);

const fetchTrainerDetails = async () => {
  if (!props.trainer?.user_id) return;

  try {
    loading.value = true;
    const response = await trainerAcademyService.adminGetTrainerProgressById(
      props.trainer.user_id
    );
    trainerDetails.value = response.data?.data || null;
  } catch (error) {
    console.error("Error fetching trainer details:", error);
    trainerDetails.value = null;
  } finally {
    loading.value = false;
  }
};

const canDownload = computed(() => {
  return trainerDetails.value?.status === "Certified";
});

const formattedRegistrationDate = computed(() => {
  if (!trainerDetails.value?.date_added) return "-";
  try {
    return new Date(trainerDetails.value.date_added).toLocaleDateString();
  } catch {
    return trainerDetails.value.date_added;
  }
});

const modules = computed(() => {
  if (!trainerDetails.value?.academy_progresses) return [];

  return trainerDetails.value.academy_progresses.map((progress) => ({
    id: progress.id,
    title: progress.title,
    status: progress.progress === 100 ? "Completed" : "In-progress",
  }));
});

const closeModal = () => {
  emit("close");
  localModelValue.value = false;
};

const downloadCertificate = async () => {
  const trainerId = props.trainer?.user_id || trainerDetails.value?.user_id;
  if (!trainerId) {
    console.error("Trainer ID not available");
    return;
  }

  try {
    isDownloadingCertificate.value = true;
    const response =
      await trainerAcademyService.adminDownloadTrainerCertificate(trainerId);

    let filename = "certificate.pdf";

    const contentDisposition =
      response.headers?.["content-disposition"] ||
      response.headers?.["Content-Disposition"];

    if (contentDisposition) {
      const patterns = [
        /filename\*=([^;]+)/i,
        /filename\*="([^"]+)"/i,
        /filename\*='([^']+)'/i,
        /filename="([^"]+)"/i,
        /filename='([^']+)'/i,
        /attachment;\s*filename\*="([^"]+)"/i,
        /attachment;\s*filename\*='([^']+)'/i,
        /attachment;\s*filename="([^"]+)"/i,
        /attachment;\s*filename='([^']+)'/i,
        /filename=([^;\s]+)/i,
        /attachment;\s*filename=([^;\s]+)/i,
      ];

      for (const pattern of patterns) {
        const match = contentDisposition.match(pattern);
        if (match && match[1]) {
          filename = match[1].trim();

          if (filename.includes("''")) {
            const parts = filename.split("''");
            if (parts.length > 1) {
              filename = decodeURIComponent(parts[1]);
            }
          } else {
            filename = filename.replace(/^["']|["']$/g, "");
          }

          break;
        }
      }
    } else {
      const trainerName =
        trainerDetails.value?.fullname ||
        props.trainer?.name ||
        props.trainer?.fullname;
      if (trainerName) {
        const sanitizedName = trainerName
          .replace(/[^a-zA-Z0-9\s-]/g, "")
          .trim()
          .replace(/\s+/g, " ");
        filename = `${sanitizedName}-trainer_certificate.pdf`;
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading certificate:", getApiError(error));
  } finally {
    isDownloadingCertificate.value = false;
  }
};
</script>

<style scoped>
.trainer-detail-modal {
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 2px 6px 2px rgba(0, 0, 0, 0.15);
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px;
  background: white;
}

.modal-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-title {
  font-size: var(--fs-xxl);
  font-weight: 700;
  color: #3b4648;
  line-height: 36px;
  margin: 0;
}

.modal-subtitle {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #6c737f;
  line-height: 24px;
  margin: 0;
  letter-spacing: 0.08px;
}

.trainer-info-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  height: 104px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-label {
  font-size: var(--fs-small);
  font-weight: 400;
  color: #6c737f;
  line-height: 20px;
  margin: 0;
  letter-spacing: 0.035px;
}

.info-value {
  font-size: var(--fs-medium);
  font-weight: 500;
  color: #3b4648;
  line-height: 24px;
  margin: 0;
  letter-spacing: 0.08px;
}

.module-progress-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  font-size: var(--fs-medium);
  font-weight: 400;
  color: #101828;
  line-height: 24px;
  margin: 0;
  letter-spacing: -0.3125px;
}

.modules-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.module-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 2px solid #d8dae5;
  border-radius: 12px;
  background: white;
}

.module-info {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 173.5px;
}

.module-number {
  font-size: 12px;
  font-weight: 400;
  color: #3b4648;
  line-height: 16px;
  margin: 0;
  letter-spacing: 0.048px;
}

.module-title {
  font-size: var(--fs-medium);
  font-weight: 700;
  color: #3b4648;
  line-height: 24px;
  margin: 0;
  letter-spacing: 0.024px;
}

.module-status {
  display: inline-block;
  padding: 10px 16px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.048px;
}

.status-completed {
  background: #56b693;
  color: white;
}

.status-in-progress {
  background: #dfe2e7;
  color: #6c737f;
}

.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  color: #6c737f;
}

.no-modules {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  color: #6c737f;
}

.skeleton-module-item {
  pointer-events: none;
}

.skeleton-module-number {
  height: 16px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 4px;
}

.skeleton-module-title {
  height: 24px;
  width: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-module-status {
  height: 36px;
  width: 100px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 999px;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

.certificate-section {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 17px;
}

.download-certificate-button {
  width: 100%;
  height: 56px;
}

.download-certificate-button:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.download-certificate-button:not(:disabled):hover {
  background: #e6e7f0;
}

.download-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  padding: 10px 16px;
  min-width: 100px;
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  background: white;
  color: var(--primary-blue);
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.024px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: #f2f3fb;
}

@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
    height: auto;
  }

  .modal-content {
    padding: 20px;
    gap: 24px;
  }

  .modal-title {
    font-size: var(--fs-xl);
  }
}
</style>
